import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  Trash2,
  Key,
  Zap,
  AlertCircle,
  Plus,
  MoreHorizontal,
  HelpCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

// Event labels - using hook for translations
const useEventLabels = () => {
  const { t } = useTranslation();
  return {
    "envelope.completed": t("webhooks.events.envelopeCompleted"),
    "envelope.sent": t("webhooks.events.envelopeSent"),
    "envelope.declined": t("webhooks.events.envelopeDeclined"),
    "envelope.voided": t("webhooks.events.envelopeVoided"),
    "envelope.expired": t("webhooks.events.envelopeExpired"),
    "recipient.signed": t("webhooks.events.recipientSigned"),
    "recipient.viewed": t("webhooks.events.recipientViewed"),
  };
};

// Loading skeleton
function ZapierSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-14 w-14 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-32 w-full" />
      <div className="grid grid-cols-3 gap-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

export default function ZapierSettings() {
  const { t } = useTranslation();
  const eventLabels = useEventLabels();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const [newKeyName, setNewKeyName] = useState("");
  const [showNewKey, setShowNewKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  // Fetch API keys
  const { data: apiKeys = [], isLoading: keysLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () =>
      api.get("/api/settings/api-keys").then((res) => res.data?.keys || []),
  });

  // Fetch Zapier subscriptions
  const { data: subscriptions = [], isLoading: subsLoading } = useQuery({
    queryKey: ["zapier-subscriptions"],
    queryFn: () =>
      api.get("/api/zapier/subscriptions").then((res) => res.data || []),
  });

  // Fetch Zapier stats
  const { data: stats } = useQuery({
    queryKey: ["zapier-stats"],
    queryFn: () => api.get("/api/zapier/stats").then((res) => res.data),
  });

  // Generate API key mutation
  const generateMutation = useMutation({
    mutationFn: (name) =>
      api.post("/api/settings/api-keys", {
        name,
        scopes: ["zapier"],
      }),
    onSuccess: (response) => {
      setShowNewKey(response.data.key);
      setNewKeyName("");
      queryClient.invalidateQueries(["api-keys"]);
      toast.success(t("zapier.keyGenerated"));
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  // Revoke API key mutation
  const revokeMutation = useMutation({
    mutationFn: (keyId) => api.delete(`/api/settings/api-keys/${keyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["api-keys"]);
      toast.success(t("zapier.keyRevoked"));
      setShowDeleteDialog(false);
      setSelectedKey(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  const copyToClipboard = async (text, keyId) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    toast.success(t("common.copied"));
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const openDeleteDialog = (key) => {
    setSelectedKey(key);
    setShowDeleteDialog(true);
  };

  const loading = keysLoading || subsLoading;

  if (loading) {
    return <ZapierSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/integrations"
            className="flex items-center justify-center h-8 w-8 rounded-lg border hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-orange-500/10">
            <Zap className="h-7 w-7 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("zapier.title")}
            </h1>
            <p className="text-muted-foreground">{t("zapier.subtitle")}</p>
          </div>
        </div>
        <Button asChild>
          <a
            href="https://zapier.com/apps/eScribe/integrations"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("zapier.openZapier")}
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>

      {/* Connect CTA Card */}
      <Card className="bg-linear-to-r from-orange-500 to-orange-600 text-white border-0">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-2">
            {t("zapier.connectTitle")}
          </h2>
          <p className="text-orange-100 mb-4">{t("zapier.connectDesc")}</p>
          <Button variant="secondary" asChild>
            <a
              href="https://zapier.com/apps/eScribe/integrations"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("zapier.getStarted")}
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">
                {t("zapier.activeConnections")}
              </p>
              <p className="text-2xl font-bold">{subscriptions.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">
                {t("zapier.eventsSent")}
              </p>
              <p className="text-2xl font-bold">{stats.deliveries24h || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">
                {t("zapier.failed")}
              </p>
              <p className="text-2xl font-bold text-destructive">
                {stats.failed24h || 0}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* New Key Alert */}
      {showNewKey && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <AlertCircle className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-800">
            {t("zapier.copyKeyNow")}
          </AlertTitle>
          <AlertDescription className="text-emerald-700">
            <p className="mb-3">{t("zapier.keyWontShow")}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white px-3 py-2 rounded border border-emerald-300 font-mono text-sm break-all">
                {showNewKey}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => copyToClipboard(showNewKey, "new")}
              >
                {copiedKey === "new" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Button
              variant="link"
              className="text-emerald-700 p-0 h-auto mt-2"
              onClick={() => setShowNewKey(null)}
            >
              {t("zapier.dismissKey")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* API Keys Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t("zapier.apiKeys")}</CardTitle>
          <CardDescription>{t("zapier.apiKeysDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Generate New Key */}
          <div className="flex items-center gap-3">
            <Input
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder={t("zapier.keyNamePlaceholder")}
              className="flex-1"
            />
            <Button
              onClick={() => generateMutation.mutate(newKeyName)}
              disabled={generateMutation.isPending || !newKeyName.trim()}
            >
              {generateMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t("zapier.generateKey")}
            </Button>
          </div>

          {/* Existing Keys */}
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t("zapier.noKeys")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("zapier.keyName")}</TableHead>
                  <TableHead>{t("zapier.keyPrefix")}</TableHead>
                  <TableHead>{t("zapier.created")}</TableHead>
                  <TableHead>{t("zapier.lastUsed")}</TableHead>
                  <TableHead className="w-12.5" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                          <Key className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <span className="font-medium">{key.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs font-mono text-muted-foreground">
                        {key.prefix}...
                      </code>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDateShort(key.createdAt, user)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {key.lastUsedAt
                        ? formatDateShort(key.lastUsedAt, user)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => openDeleteDialog(key)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t("common.revoke")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Active Connections Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("zapier.activeConnections")}</CardTitle>
              <CardDescription>
                {t("zapier.activeConnectionsDesc")}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                queryClient.invalidateQueries(["zapier-subscriptions"])
              }
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t("zapier.noConnections")}</p>
              <p className="text-sm">{t("zapier.createZap")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("zapier.event")}</TableHead>
                  <TableHead>{t("zapier.status")}</TableHead>
                  <TableHead>{t("zapier.connected")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-2 w-2 rounded-full ${
                            sub.status === "active"
                              ? "bg-emerald-500"
                              : "bg-muted-foreground"
                          }`}
                        />
                        <span className="font-medium">
                          {eventLabels[sub.event] || sub.event}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          sub.status === "active" ? "outline" : "secondary"
                        }
                        className={
                          sub.status === "active"
                            ? "bg-emerald-500/10 text-emerald-600 border-emerald-200"
                            : ""
                        }
                      >
                        {sub.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDateShort(sub.created_at, user)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-muted/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <HelpCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">{t("zapier.needHelp")}</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  <a
                    href="/docs/zapier"
                    className="text-primary hover:underline"
                  >
                    {t("zapier.viewGuide")} →
                  </a>
                </li>
                <li>
                  <a
                    href="https://zapier.com/help"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {t("zapier.zapierHelp")} →
                  </a>
                </li>
                <li>
                  <a href="/support" className="text-primary hover:underline">
                    {t("zapier.contactSupport")} →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("zapier.revokeKey")}</DialogTitle>
            <DialogDescription>
              {t(
                "zapier.revokeConfirm",
                'Are you sure you want to revoke "{{name}}"? Any Zaps using this key will stop working.',
                { name: selectedKey?.name },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => revokeMutation.mutate(selectedKey?.id)}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending
                ? t("common.revoking")
                : t("common.revoke")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
